import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@ludus/ui'
import type { AuditCounters } from '../../types.ts'

interface CoveragePanelProps {
  counters: AuditCounters
  blockedBy: string[]
}

/** Coverage counters — what the slice holds and what it lacks. Never an anomaly (D-14). */
export function CoveragePanel({ counters, blockedBy }: CoveragePanelProps) {
  const { t } = useTranslation()
  const calls = counters.calls.withAudio + counters.calls.withoutAudio
  const contacts = counters.contacts.withPhone + counters.contacts.withoutPhone
  return (
    <Card aria-label={t('pages.audits.coverage.title', { defaultValue: 'Coverage' })}>
      <CardHeader>
        <CardTitle>{t('pages.audits.coverage.title', { defaultValue: 'Coverage' })}</CardTitle>
        <CardDescription>
          {t('pages.audits.coverage.description', {
            defaultValue:
              'What the slice holds. A gap here is a fact about the data, not an error.',
          })}
        </CardDescription>
      </CardHeader>
      <CardBody>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {Object.entries(counters.cardsPerPipeline).map(([pipelineId, n]) => (
            <Row
              key={pipelineId}
              label={t('pages.audits.coverage.cards', {
                defaultValue: 'Cards · {{pipeline}}',
                pipeline: pipelineId,
              })}
              value={n}
            />
          ))}
          <Row
            label={t('pages.audits.coverage.callsWithAudio', { defaultValue: 'Calls with audio' })}
            value={`${counters.calls.withAudio} / ${calls}`}
          />
          <Row
            label={t('pages.audits.coverage.callsWithoutAudio', {
              defaultValue: 'Calls without audio',
            })}
            value={counters.calls.withoutAudio}
          />
          <Row
            label={t('pages.audits.coverage.contactsWithPhone', {
              defaultValue: 'Contacts with a phone',
            })}
            value={`${counters.contacts.withPhone} / ${contacts}`}
          />
          <Row
            label={t('pages.audits.coverage.contactsWithoutPhone', {
              defaultValue: 'Contacts without a phone',
            })}
            value={counters.contacts.withoutPhone}
          />
        </dl>
        {blockedBy.length > 0 && (
          <p role="status" className="mt-3 text-xs text-toast-warning-text">
            {t('pages.audits.coverage.blocked', {
              defaultValue: 'Cannot reach ready: {{reasons}}. The customer decides in the queue.',
              reasons: blockedBy
                .map((b) => t(`pages.audits.coverage.blockers.${b}`, { defaultValue: b }))
                .join(', '),
            })}
          </p>
        )}
      </CardBody>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium text-foreground">{value}</dd>
    </>
  )
}
